import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './States.css';
import InputBoxLarge from '../../../components/common/InputBox/InputBoxLarge';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import CountrySelectWithFlag from '../../../components/common/SelectBox/CountrySelectWithFlag';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';
import { isTextOnly } from '../../../validation';
import useRefreshStore from '../../../store/useRefreshStore';
import { fetchActiveCountries } from '../../../services/Localization/CountryService';

const StateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { triggerRefresh } = useRefreshStore();

  const [countryOptions, setCountryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: '',
    state_code: '',
    country: '',
  });

  useEffect(() => {
    const init = async () => {
      await loadCountries();
      await fetchStateDetail();
    };
    init();
  }, [id]);

  const loadCountries = async () => {
    try {
      const countries = await fetchActiveCountries();
      setCountryOptions(countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountryOptions([]);
    }
  };

  const fetchStateDetail = async () => {
    try {
      setLoading(true);
      const response = await authenticatedAxios.get(`${baseurl}localization/state/?id=${id}`);
      if (response.data && response.data.data) {
        const stateData = response.data.data;
        setForm({
          name: stateData.name || '',
          state_code: stateData.state_code || '',
          country: stateData.country?.id || stateData.country || '',
        });
      } else {
        setErrors({ general: 'State not found.' });
      }
    } catch (error) {
      console.error('Error fetching state details:', error);
      if (error?.response?.data?.error_code === 1001) {
        navigate('/permission-denied');
      } else {
        setErrors({ general: 'Failed to load state details.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[key];
      delete updated.general;
      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name || !form.name.trim()) {
      newErrors.name = ['State Name is required'];
    } else {
      const textOnly = isTextOnly(form.name);
      if (textOnly !== true) {
        newErrors.name = [textOnly];
      }
    }

    if (!form.state_code || !form.state_code.trim()) {
      newErrors.state_code = ['State Code is required'];
    }

    if (!form.country) {
      newErrors.country = ['Country is required'];
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('name', form.name.trim());
    formData.append('state_code', form.state_code.trim());
    formData.append('country', form.country);

    try {
      const response = await authenticatedAxios.put(
        `${baseurl}localization/state/?id=${id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data && response.data.response_code === 1) {
        triggerRefresh('States');
        SuccessAlert('State updated successfully');
        navigate(`/states/detail/${id}`);
      } else {
        const msg = response.data?.message;
        if (msg && typeof msg === 'object') {
          setErrors(msg);
        } else {
          setErrors({ general: msg || 'Failed to update state.' });
        }
      }
    } catch (error) {
      console.error('Error updating state:', error);
      if (error.response?.data?.message) {
        const msg = error.response.data.message;
        if (typeof msg === 'object') {
          setErrors(msg);
        } else {
          setErrors({ general: msg });
        }
      } else {
        setErrors({ general: 'Failed to update state. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="whole-container">
        <p className="common-text fts-14 ftw-4" style={{ padding: '20px' }}>
          Loading state details...
        </p>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization / States /"
          nestedpageName="Edit"
          linkTo="/states"
        />
        <div className="export-container">
          <ExportLabel />
        </div>
      </div>

      <div className="page-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        {/* State Details Card */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">State Details</span>
            <button
              className="popup-add-button common-text white fts-15 ftw-5"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          <div className="card-body">
            <InputBoxLarge
              label="State Name"
              defaultValue={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              isRequired
              error={errors.name}
            />

            <div className="row-2">
              <InputBoxSmall
                label="State Code"
                defaultValue={form.state_code}
                onChange={(e) => handleChange('state_code', e.target.value)}
                isRequired
                error={errors.state_code}
              />
              <CountrySelectWithFlag
                label="Country"
                options={countryOptions}
                value={form.country}
                onChange={(e) => handleChange('country', e.target.value)}
                placeholder="Select Country"
                isRequired
                error={errors.country}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateEdit;
